/**
 * GitHub SDK Types
 *
 * Type definitions for the Morph GitHub integration.
 * Users connect GitHub in the Morph dashboard, then use these
 * types to interact with their GitHub repos through the SDK.
 */

// =============================================================================
// Configuration
// =============================================================================

/**
 * Configuration for the GitHub client
 */
export interface GitHubClientConfig {
  /** Morph API key (defaults to MORPH_API_KEY env var) */
  apiKey?: string;
  /** Default installation ID to use (optional, can be specified per-request) */
  installationId?: string;
  /** Base URL for the Morph API (defaults to https://api.morphllm.com) */
  baseUrl?: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Enable debug logging */
  debug?: boolean;
}

// =============================================================================
// Core Types
// =============================================================================

/**
 * A GitHub App installation (connected GitHub account)
 */
export interface Installation {
  /** Installation ID (use this for subsequent requests) */
  id: string;
  /** GitHub account login (username or org name) */
  accountLogin: string;
  /** Account type */
  accountType: "User" | "Organization";
  /** Custom display name (set in Morph dashboard) */
  displayName?: string;
  /** Whether check runs are disabled (only PR comments posted) */
  disableCheckRun: boolean;
  /** Whether to keep all previous PR comments (vs. replacing with latest) */
  keepCommentHistory: boolean;
  /** Browser automation model used for preview tests */
  model: string;
}

/**
 * Input for updating installation settings
 */
export interface UpdateInstallationInput {
  /** Installation ID to update */
  installationId: string;
  /** Custom display name */
  displayName?: string;
  /** Whether to disable check runs */
  disableCheckRun?: boolean;
  /** Whether to keep comment history */
  keepCommentHistory?: boolean;
  /** Browser automation model */
  model?: string;
}

/**
 * Site authentication credentials for a repo or project
 */
export interface SiteAuth {
  /** Login username or email */
  username: string | null;
  /** Login password */
  password: string | null;
}

/**
 * A repository with its Morph configuration
 */
export interface RepoConfig {
  /** Database UUID — use this for update/remove operations */
  id: string;
  /** GitHub's numeric repo ID */
  githubId: number;
  /** Full name including owner (e.g., "acme/app") */
  fullName: string;
  /** Repository name (e.g., "app") */
  name: string;
  /** Whether Morph tests are enabled */
  enabled: boolean;
  /** Whether to also test on mobile viewport */
  testMobile: boolean;
  /** Path filter — only trigger tests when files match this prefix */
  pathFilter: string | null;
  /** Custom instructions for the AI agent */
  customInstructions: string | null;
  /** Site authentication credentials */
  auth: SiteAuth | null;
}

/**
 * Input for updating a repo's configuration
 */
export interface UpdateRepoInput {
  /** Repo DB ID (from RepoConfig.id) */
  repoId: string;
  /** Whether Morph tests are enabled */
  enabled?: boolean;
  /** Whether to also test on mobile viewport */
  testMobile?: boolean;
  /** Path filter for conditional triggers */
  pathFilter?: string | null;
  /** Custom instructions for the AI agent */
  customInstructions?: string | null;
  /** Site auth credentials (null to clear) */
  auth?: { username?: string; password?: string; cookies?: unknown } | null;
}

/**
 * Input for syncing repos from GitHub
 */
export interface SyncReposInput {
  /** Installation ID to sync repos for */
  installationId: string;
}

/**
 * A monorepo sub-project scoped under a repo
 */
export interface Project {
  /** Project DB UUID */
  id: string;
  /** Parent repo DB UUID */
  repoId: string;
  /** Project name */
  name: string;
  /** Path filter within the repo */
  pathFilter: string | null;
  /** Whether tests are enabled */
  enabled: boolean;
  /** Whether to test on mobile viewport */
  testMobile: boolean;
  /** Custom instructions for the AI agent */
  customInstructions: string | null;
  /** Site authentication credentials */
  auth: SiteAuth | null;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
}

/**
 * Input for creating a project
 */
export interface CreateProjectInput {
  /** Parent repo DB ID */
  repoId: string;
  /** Project name (must be unique within the repo) */
  name: string;
  /** Path filter within the repo (e.g., "packages/web/") */
  pathFilter: string;
  /** Custom instructions for the AI agent */
  customInstructions?: string;
}

/**
 * Input for updating a project
 */
export interface UpdateProjectInput {
  /** Project DB ID */
  projectId: string;
  /** Whether tests are enabled */
  enabled?: boolean;
  /** Whether to test on mobile viewport */
  testMobile?: boolean;
  /** Path filter within the repo */
  pathFilter?: string | null;
  /** Custom instructions for the AI agent */
  customInstructions?: string | null;
  /** Site auth credentials (null to clear) */
  auth?: { username?: string; password?: string; cookies?: unknown } | null;
}

/**
 * Input for listing projects
 */
export interface ListProjectsInput {
  /** Parent repo DB ID */
  repoId: string;
}

/**
 * A GitHub repository
 */
export interface Repo {
  /** GitHub's numeric repo ID */
  id: number;
  /** Repository name (e.g., "app") */
  name: string;
  /** Full name including owner (e.g., "acme/app") */
  fullName: string;
  /** Whether the repo is private */
  private: boolean;
  /** Default branch name */
  defaultBranch?: string;
}

/**
 * A pull request (basic info)
 */
export interface PullRequest {
  /** PR number */
  number: number;
  /** PR title */
  title: string;
  /** PR description/body */
  body: string | null;
  /** PR state */
  state: "open" | "closed";
  /** Author's GitHub username */
  author: string;
  /** HEAD commit SHA */
  headSha: string;
  /** Base branch name */
  baseBranch: string;
  /** HEAD branch name */
  headBranch: string;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last update timestamp (ISO 8601) */
  updatedAt: string;
}

/**
 * A pull request with full context (diff and files)
 */
export interface PullRequestWithContext extends PullRequest {
  /** Full unified diff */
  diff: string;
  /** List of changed files with patches */
  files: FileChange[];
}

/**
 * A file changed in a pull request
 */
export interface FileChange {
  /** File path */
  filename: string;
  /** Change status */
  status: "added" | "removed" | "modified" | "renamed";
  /** Number of lines added */
  additions: number;
  /** Number of lines deleted */
  deletions: number;
  /** Unified diff patch for this file */
  patch?: string;
}

/**
 * A deployment (e.g., Vercel preview)
 */
export interface Deployment {
  /** Deployment ID */
  id: number;
  /** Commit SHA this deployment is for */
  sha: string;
  /** Environment name (e.g., "preview", "production") */
  environment: string;
  /** Deployment state */
  state: "pending" | "success" | "failure" | "error" | "inactive" | "in_progress" | "queued";
  /** Preview/deployment URL (if available) */
  url: string | null;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
}

/**
 * A comment on a PR/issue
 */
export interface Comment {
  /** Comment ID */
  id: number;
  /** Comment body/content */
  body: string;
  /** Author's GitHub username */
  author: string;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last update timestamp (ISO 8601) */
  updatedAt: string;
}

/**
 * A GitHub check run (CI status)
 */
export interface CheckRun {
  /** Check run ID */
  id: number;
  /** Check run name */
  name: string;
  /** Current status */
  status: "queued" | "in_progress" | "completed";
  /** Conclusion (only set when status is "completed") */
  conclusion?:
    | "success"
    | "failure"
    | "neutral"
    | "cancelled"
    | "skipped"
    | "timed_out"
    | "action_required";
  /** Start timestamp (ISO 8601) */
  startedAt?: string;
  /** Completion timestamp (ISO 8601) */
  completedAt?: string;
}

// =============================================================================
// Input Types
// =============================================================================

/**
 * Input for listing repositories
 */
export interface ListReposInput {
  /** Installation ID to list repos for */
  installationId: string;
}

/**
 * Input for listing pull requests
 */
export interface ListPullRequestsInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** Filter by PR state (default: "open") */
  state?: "open" | "closed" | "all";
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for getting a single pull request
 */
export interface GetPullRequestInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** PR number */
  number: number;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for listing deployments
 */
export interface ListDeploymentsInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** Filter by commit SHA */
  sha?: string;
  /** Filter by environment name */
  environment?: string;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for listing comments on a PR
 */
export interface ListCommentsInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** PR number */
  pr: number;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for creating a comment
 */
export interface CreateCommentInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** PR number */
  pr: number;
  /** Comment body (Markdown supported) */
  body: string;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for updating a comment
 */
export interface UpdateCommentInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** Comment ID to update */
  commentId: number;
  /** New comment body */
  body: string;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for deleting a comment
 */
export interface DeleteCommentInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** Comment ID to delete */
  commentId: number;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for creating a check run
 */
export interface CreateCheckRunInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** Commit SHA to create check run for */
  sha: string;
  /** Check run name (e.g., "Preview Test") */
  name: string;
  /** Initial status */
  status: "queued" | "in_progress";
  /** Output title */
  title?: string;
  /** Output summary (Markdown supported) */
  summary?: string;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

/**
 * Input for updating a check run
 */
export interface UpdateCheckRunInput {
  /** Repository owner */
  owner: string;
  /** Repository name */
  repo: string;
  /** Check run ID to update */
  checkRunId: number;
  /** New status */
  status?: "queued" | "in_progress" | "completed";
  /** Conclusion (required if status is "completed") */
  conclusion?: CheckRun["conclusion"];
  /** Output title */
  title?: string;
  /** Output summary (Markdown supported) */
  summary?: string;
  /** Output text (detailed info, Markdown supported) */
  text?: string;
  /** Installation ID (uses default if not specified) */
  installationId?: string;
}

// =============================================================================
// Error Types
// =============================================================================

/**
 * Error thrown by GitHub operations
 */
export class GitHubError extends Error {
  /** HTTP status code */
  status: number;
  /** Error code from API */
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "GitHubError";
    this.status = status;
    this.code = code;
  }
}

/**
 * Error thrown when no installation is found or specified
 */
export class NoInstallationError extends GitHubError {
  constructor(message = "No GitHub installation found. Connect GitHub at morphllm.com/dashboard/integrations/github") {
    super(message, 404, "NO_INSTALLATION");
  }
}

/**
 * Error thrown when resource is not found
 */
export class NotFoundError extends GitHubError {
  constructor(resource: string) {
    super(`${resource} not found`, 404, "NOT_FOUND");
  }
}

/**
 * Error thrown when user lacks permission
 */
export class PermissionError extends GitHubError {
  constructor(message = "Insufficient permissions for this operation") {
    super(message, 403, "PERMISSION_DENIED");
  }
}
